<template>
  <popup-dialog :full-width="true" :popup-active="true">
    <i @click="emit('close')" class="fas fa-times popup-close"></i>
    <div class="popup popup--settings bg--white map-picker-popup">
      <div class="popup__content map-picker scrollbar-custom">
        <h2 class="popup-headline map-picker__title">{{ t('popup.link.pickerTitle') }}</h2>
        <p class="map-picker__hint">
          {{ t('popup.link.pickerHint') }}
          <span v-if="picked?.kind === 'node'">{{ t('popup.link.pickerNodeHint') }}</span>
        </p>

        <div v-if="isBlocked" class="alert error">
          <p class="alert__content">{{ t('popup.link.pickerBlocked') }}</p>
        </div>

        <div v-if="!isBlocked" class="map-picker__frame">
          <iframe
            ref="frameRef"
            :src="previewSrc"
            class="map-picker__iframe"
            title="map"
            @load="onFrameLoad"
          ></iframe>
        </div>

        <!-- Falls back to a plain list when the tenant forbids framing the map. -->
        <ul v-else class="map-picker__nodes scrollbar-custom">
          <li v-for="node in nodeOptions" :key="node.id">
            <button type="button" class="map-picker__node" @click="pickNode(node.id)">
              {{ node.label }}
            </button>
          </li>
        </ul>

        <div class="map-picker__footer">
          <span class="map-picker__chosen">{{ chosenLabel }}</span>
          <div class="button-wrapper">
            <div
              @click="confirm"
              :class="{ disabled: !picked }"
              aria-label="deeplink-use"
              class="button button--popup bg--sea mr--15"
            >
              <i class="fas fa-check"></i> <span>{{ t('popup.link.pickerUse') }}</span>
            </div>
            <div
              @click="emit('close')"
              aria-label="deeplink-cancel"
              class="button button--popup"
            >
              <i class="fas fa-times"></i> <span>{{ t('popup.link.cancel') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </popup-dialog>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import PopupDialog from '@/components/ui/popup/PopupDialog.vue';
import {
  buildNodeDeeplink,
  describeDeeplink,
  isMapStateHash,
  parseDeeplink,
  type DeeplinkTarget,
} from '@/modules/editor-link';

interface NodeOption {
  id: number;
  label: string;
}

const emit = defineEmits<{ close: []; chosen: [href: string] }>();

const { t } = useI18n();
const store = useStore();

const picked = ref<DeeplinkTarget | null>(null);
const isBlocked = ref(false);
const frameRef = ref<HTMLIFrameElement | null>(null);

const baseURL = import.meta.env.VITE_ROOT_HOST;
const mapCurrent = computed(() => store.getters['maps/getCurrentMap']);
const previewSrc = computed<string>(() => `${baseURL}${mapCurrent.value?._links?.preview_draft ?? ''}`);
const previewOrigin = computed<string>(() => {
  try {
    return new URL(previewSrc.value, window.location.href).origin;
  } catch {
    return '';
  }
});

const nodeOptions = computed<NodeOption[]>(() =>
  (store.getters['nodes/getRawNodes'] ?? []).map((node: { id: number }) => ({
    id: node.id,
    label: `${t('popup.link.pickerNodeFallback')} ${node.id}`,
  }))
);

const chosenLabel = computed<string>(() => {
  if (!picked.value) return t('popup.link.pickerEmpty');
  return picked.value.kind === 'node'
    ? t('popup.link.pickerNode', { id: picked.value.nodeId })
    : t('popup.link.pickerArea', { layer: picked.value.layer });
});

// The embedded Map Viewer posts its own hash on every state change, so the deeplink
// arrives ready-made — see map-init.js. connectIFrame() is deliberately not used:
// it would rewrite the Map Builder's address bar and can trip the map's embed-domain
// guard.
function onMessage(event: MessageEvent): void {
  if (previewOrigin.value && event.origin !== previewOrigin.value) return;
  if (frameRef.value && event.source !== frameRef.value.contentWindow) return;

  const data = event.data;
  if (typeof data !== 'string') return;
  if (data === 'kontextlab:embedCloseRequest') {
    emit('close');
    return;
  }
  if (!isMapStateHash(data)) return;
  picked.value = parseDeeplink(data);
}

function onFrameLoad(): void {
  // A refused frame leaves an unreachable document behind; treat that as blocked
  // rather than showing an empty white box.
  try {
    isBlocked.value = !frameRef.value?.contentWindow;
  } catch {
    isBlocked.value = true;
  }
}

function pickNode(nodeId: number): void {
  picked.value = { kind: 'node', nodeId };
}

function confirm(): void {
  if (!picked.value) return;
  const href =
    picked.value.kind === 'node'
      ? buildNodeDeeplink(picked.value.nodeId)
      : describeDeeplink(picked.value);
  emit('chosen', href);
}

onMounted(() => window.addEventListener('message', onMessage));
onBeforeUnmount(() => window.removeEventListener('message', onMessage));
</script>

<style lang="scss">
// The popup itself must not scroll — .map-picker owns the scrolling, so the
// map keeps its minimum height instead of collapsing into a strip.
.map-picker-popup {
  overflow: hidden;
}

.map-picker__title {
  flex: none;
}

.map-picker {
  display: flex;
  flex-direction: column;
  height: 100%;
  // Once the map hits its minimum the content no longer fits, and this is where
  // it scrolls.
  overflow-y: auto;
  box-sizing: border-box;
  width: 100%;
  padding: 32px 40px;
}

.map-picker__hint {
  flex: none;
  margin-bottom: 10px;
}

.map-picker__frame {
  position: relative;
  // Shrinks with the popup down to this, then the popup scrolls instead — a
  // flex child would otherwise collapse to nothing and leave a bare strip.
  flex: 1 1 auto;
  min-height: 300px;
}

.map-picker__iframe {
  width: 100%;
  height: 100%;
  border: 0;
}

.map-picker__nodes {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  list-style: none;
}

.map-picker__node {
  padding: 6px 10px;
  border: 0;
  background: transparent;
  font: inherit;
  cursor: pointer;
}

.map-picker__footer {
  // Never squeezed out: the actions have to stay reachable at any height.
  flex: none;
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 15px;

  // .button--popup fixes a 200px width, which is far wider than these two
  // labels need.
  .button {
    min-width: 0;
  }
}

.map-picker__chosen {
  flex: 1 1 auto;
}
</style>
